import type { RbimDataFile } from "@rbim/shared";

type ExportRecord = RbimDataFile["records"][number];
type Member = ExportRecord["rawData"]["members"][number];
type CellValue = string | number | boolean | null | undefined;
type HouseholdReader = (record: ExportRecord) => CellValue;
type ResidentReader = (record: ExportRecord, member: Member, memberIndex: number) => CellValue;

type Column<Reader> = {
  heading: string;
  read: Reader;
};

const householdColumns: Array<Column<HouseholdReader>> = [
  col("Submission ID", (r) => r.id ?? ""),
  col("Household ID", householdId),
  col("Submitted At", (r) => r.createdAt ?? ""),
  col("Household Head Resident ID", (r) => {
    const members = r.rawData?.members ?? [];
    const index = members.findIndex((member) => member && isHouseholdHead(member.relationshipToHead));
    return index >= 0 ? residentId(r, members[index], index) : "";
  }),
  col("Province", (r) => r.rawData?.household?.province ?? ""),
  col("Province / HUC Code", (r) => r.rawData?.household?.provinceCode ?? ""),
  col("City / Municipality", (r) => r.rawData?.household?.municipality ?? ""),
  col("City / Municipality Code", (r) => r.rawData?.household?.municipalityCode || r.municipalityCode || ""),
  col("Barangay", (r) => r.rawData?.household?.barangay ?? ""),
  col("Barangay Code", (r) => r.rawData?.household?.barangayCode || r.barangayCode || ""),
  col("Purok / Sitio", (r) => r.rawData?.household?.purok ?? ""),
  col("Address", (r) => r.rawData?.household?.address ?? ""),
  col("Household Number", (r) => r.rawData?.household?.householdCode ?? ""),
  col("Household Family", (r) => r.rawData?.household?.family ?? ""),
  col("Family Relationship to Household Head", (r) => readExtra(r.rawData?.household, "familyRelationshipToHouseholdHead")),
  col("Household Type", (r) => r.rawData?.household?.householdType ?? ""),
  col("Name of Respondent", (r) => r.rawData?.household?.respondentName ?? ""),
  col("Household Head", (r) => r.rawData?.household?.householdHead ?? ""),
  col("Declared Total Household Members", (r) => r.rawData?.household?.totalMembers ?? ""),
  col("Declared Total Families", (r) => uniqueFamilyCount(r.rawData?.members ?? [])),
  ...interviewColumns(0, "1st"),
  ...interviewColumns(1, "2nd"),
  col("Date Encoded", (r) => r.rawData?.encoding?.dateEncoded ?? ""),
  col("Encoder", (r) => r.rawData?.encoding?.encoder ?? ""),
  col("Encoding Supervisor", (r) => r.rawData?.encoding?.supervisor ?? ""),
  ...householdQuestionColumns(),
  col("Actual Exported Member Count", (r) => (r.rawData?.members ?? []).length),
  col("Actual Family Unit Count", (r) => uniqueFamilyCount(r.rawData?.members ?? [])),
  col("Source System", () => "New RBIM"),
  col("Import Mode", () => "New Encoding"),
  col("Import Status", () => "Clean"),
  col("Imported At", (r) => readExtra(r.cleanedData, "importedAt")),
  col("Review Warnings", (r) => stringifyList(readExtra(r.cleanedData, "reviewWarnings"))),
  col("Legacy Source Row Numbers", (r) => stringifyList(readNested(r.cleanedData, ["legacyMigration", "rowNumbers"]))),
  col("Legacy Preserved Raw Rows", (r) => stringifyJson(readNested(r.cleanedData, ["legacyMigration", "rawRows"]))),
  col("Data Quality Issues", (r) => stringifyList(readExtra(r.cleanedData, "dataQualityIssues"))),
];

const residentColumns: Array<Column<ResidentReader>> = [
  rcol("Submission ID", (r) => r.id ?? ""),
  rcol("Household ID", (r) => householdId(r)),
  rcol("Household Number", (r) => r.rawData?.household?.householdCode ?? ""),
  rcol("Submitted At", (r) => r.createdAt ?? ""),
  rcol("Resident ID", (r, m, i) => residentId(r, m, i)),
  rcol("Line Number", (_r, _m, i) => i + 1),
  rcol("Family Unit ID", (r, m) => familyUnitId(r, m)),
  rcol("Q1 Suffix / Name Extension", (_r, m) => readExtra(m, "suffix")),
  rcol("Computed Age (Years)", (_r, m) => computeAge(m).years),
  rcol("Computed Age Display", (_r, m) => computeAge(m).display),
  rcol("Province", (r) => r.rawData?.household?.province ?? ""),
  rcol("City / Municipality", (r) => r.rawData?.household?.municipality ?? ""),
  rcol("Barangay", (r) => r.rawData?.household?.barangay ?? ""),
  rcol("Purok / Sitio", (r) => r.rawData?.household?.purok ?? ""),
  rcol("Address", (r) => r.rawData?.household?.address ?? ""),
  rcol("Family Unit Assignment", (_r, m) => m?.familyUnitAssignment ?? ""),
  rcol("Saved Family Unit ID", (_r, m) => m?.familyUnitId ?? ""),
  rcol("Family Unit Number", (_r, m) => m?.familyUnitNo ?? ""),
  rcol("Family Name", (_r, m) => m?.familyName ?? ""),
  rcol("Relationship to Family Head", (_r, m) => m?.relationshipToFamilyHead ?? ""),
  rcol("Is Family Head", (_r, m) => m?.isFamilyHead ?? false),
  rcol("Canonical Resident ID", (r, m, i) => residentId(r, m, i)),
  rcol("Q1 First Name", (_r, m) => m?.firstName ?? ""),
  rcol("Q1 Middle Name", (_r, m) => m?.middleName ?? ""),
  rcol("Q1 Surname", (_r, m) => m?.lastName ?? ""),
  rcol("Q2 Relationship to Household Head", (_r, m) => m?.relationshipToHead ?? ""),
  rcol("Q3 Sex", (_r, m) => m?.sex ?? ""),
  rcol("Q4 Reported Age", (_r, m) => m?.reportedAge ?? ""),
  rcol("Q5 Date of Birth", (_r, m) => m?.dateOfBirth ?? ""),
  rcol("Q5 Date of Birth Unknown", (_r, m) => m?.dateOfBirthUnknown ?? false),
  rcol("Q5 Birthdate Precision", (_r, m) => m?.birthdatePrecision ?? ""),
  rcol("Q4/Q5 Age As Of Date", (_r, m) => m?.ageAsOfDate ?? ""),
  rcol("Q4/Q5 Age Source", (_r, m) => m?.ageSource ?? ""),
  rcol("Q6 Place of Birth", (_r, m) => m?.placeOfBirth ?? ""),
  rcol("Q7 Nationality", (_r, m) => m?.nationality ?? ""),
  rcol("Q7 Nationality Detail", (_r, m) => m?.nationalityOther ?? ""),
  rcol("Q8 Marital Status", (_r, m) => m?.civilStatus ?? ""),
  rcol("Q9 Religion", (_r, m) => m?.religion ?? ""),
  rcol("Q10 Ethnicity", (_r, m) => m?.ethnicity ?? ""),
  rcol("Q11 Highest Level of Education", (_r, m) => m?.highestEducation ?? ""),
  rcol("Q12 Currently Enrolled", (_r, m) => m?.currentlyEnrolled ?? ""),
  rcol("Q13 School Level", (_r, m) => m?.schoolLevel ?? ""),
  rcol("Q14 Place of School", (_r, m) => m?.placeOfSchool ?? ""),
  rcol("Q15 Reason for Not Attending School", (_r, m) => m?.reasonNotAttendingSchool ?? ""),
  rcol("Q15 Other Reason Detail", (_r, m) => m?.reasonNotAttendingSchoolOther ?? ""),
  rcol("Q16 Monthly Income", (_r, m) => m?.monthlyIncome ?? ""),
  rcol("Q17 Source of Income", (_r, m) => m?.sourceOfIncome ?? ""),
  rcol("Q17 Source of Income Detail", (_r, m) => m?.sourceOfIncomeOther ?? ""),
  rcol("Q18 Status of Work / Business", (_r, m) => m?.workStatus ?? ""),
  rcol("Q19 Place of Work / Business", (_r, m) => m?.placeOfWork ?? ""),
  rcol("Q20 Place of Delivery", (_r, m) => m?.placeOfDelivery ?? ""),
  rcol("Q20 Place of Delivery Detail", (_r, m) => m?.placeOfDeliveryOther ?? ""),
  rcol("Q21 Birth Attendant", (_r, m) => m?.birthAttendant ?? ""),
  rcol("Q21 Birth Attendant Detail", (_r, m) => m?.birthAttendantOther ?? ""),
  rcol("Q22 Immunization", (_r, m) => m?.immunization ?? ""),
  rcol("Q23 Total Pregnancies", (_r, m) => m?.livingChildren ?? ""),
  rcol("Q23 Living Children", (_r, m) => m?.livingChildrenAlive ?? ""),
  rcol("Q24 Family Planning Use", (_r, m) => m?.familyPlanningUse ?? ""),
  rcol("Q24 Family Planning Detail", (_r, m) => m?.familyPlanningUseOther ?? ""),
  rcol("Q25 Source of FP Method", (_r, m) => m?.fpMethodSource ?? ""),
  rcol("Q25 Source of FP Detail", (_r, m) => m?.fpMethodSourceOther ?? ""),
  rcol("Q26 Intention to Use FP", (_r, m) => m?.intentionToUseFp ?? ""),
  rcol("Q26 FP Method / Reason Detail", (_r, m) => m?.intentionToUseFpDetail ?? ""),
  rcol("Q27 Health Insurance", (_r, m) => m?.healthInsurance ?? ""),
  rcol("Q27 Health Insurance Detail", (_r, m) => m?.healthInsuranceOther ?? ""),
  rcol("Q28 Facility Visited in Past 12 Months", (_r, m) => m?.facilityVisitedPast12Months ?? ""),
  rcol("Q28 Facility Detail", (_r, m) => m?.facilityVisitedPast12MonthsOther ?? ""),
  rcol("Q29 Health Facility Visit Reason", (_r, m) => m?.healthFacilityVisitReason ?? ""),
  rcol("Q29 Visit Reason Detail", (_r, m) => m?.healthFacilityVisitReasonOther ?? ""),
  rcol("Q30 Disability", (_r, m) => m?.disability ?? ""),
  rcol("Q31 Solo Parent", (_r, m) => m?.soloParentStatus ?? ""),
  rcol("Q32 Registered Senior Citizen", (_r, m) => m?.registeredSeniorCitizen ?? ""),
  rcol("Q33 Registered Voter in Barangay", (_r, m) => m?.registeredVoterBarangay ?? ""),
  rcol("Q34 Length of Stay in Barangay", (_r, m) => m?.lengthOfStayInBarangay ?? ""),
  rcol("Q35 Previous Residence - Five Years Ago", (_r, m) => m?.previousResidenceFiveYears ?? ""),
  rcol("Q36 Previous Residence - Six Months Ago", (_r, m) => m?.previousResidenceSixMonths ?? ""),
  rcol("Q37 Type of Resident", (_r, m) => m?.residentType ?? ""),
  rcol("Q38 Date of Transfer", (_r, m) => m?.dateOfTransfer ?? ""),
  rcol("Q39A Reason for Leaving Previous Residence", (_r, m) => m?.reasonLeavingPreviousResidence1 ?? ""),
  rcol("Q39A Other Reason Detail", (_r, m) => m?.reasonLeavingPreviousResidence1Other ?? ""),
  rcol("Q39B Reason for Leaving Previous Residence", (_r, m) => m?.reasonLeavingPreviousResidence2 ?? ""),
  rcol("Q39B Other Reason Detail", (_r, m) => m?.reasonLeavingPreviousResidence2Other ?? ""),
  rcol("Q39C Reason for Leaving Previous Residence", (_r, m) => m?.reasonLeavingPreviousResidence3 ?? ""),
  rcol("Q39C Other Reason Detail", (_r, m) => m?.reasonLeavingPreviousResidence3Other ?? ""),
  rcol("Q40 Return to Previous Residence", (_r, m) => m?.returnToPreviousResidence ?? ""),
  rcol("Q40 Return Date / Detail", (_r, m) => m?.returnToPreviousResidenceDetail ?? ""),
  rcol("Q41A Reason for Transferring to Barangay", (_r, m) => m?.transferReason1 ?? ""),
  rcol("Q41A Other Transfer Reason", (_r, m) => m?.transferReason1Other ?? ""),
  rcol("Q41B Reason for Transferring to Barangay", (_r, m) => m?.transferReason2 ?? ""),
  rcol("Q41B Other Transfer Reason", (_r, m) => m?.transferReason2Other ?? ""),
  rcol("Q41C Reason for Transferring to Barangay", (_r, m) => m?.transferReason3 ?? ""),
  rcol("Q41C Other Transfer Reason", (_r, m) => m?.transferReason3Other ?? ""),
  rcol("Q42 Duration of Stay in Current Barangay", (_r, m) => m?.durationOfStayCurrentBarangay ?? ""),
  rcol("Q42 Duration Date / Detail", (_r, m) => m?.durationOfStayCurrentBarangayDetail ?? ""),
  rcol("Q43A Has Valid CTC", (_r, m) => m?.hasValidCtc ?? ""),
  rcol("Q43B CTC Issued in Barangay", (_r, m) => m?.ctcIssuedInBarangay ?? ""),
  rcol("Q44 Skills Development Training Interest", (_r, m) => m?.skillsTrainingInterest ?? ""),
  rcol("Q45 Skills", (_r, m) => m?.skills ?? ""),
  rcol("Q45 Skills Detail", (_r, m) => m?.skillsOther ?? ""),
  rcol("Legacy Occupation (Supplemental)", (_r, m) => m?.occupation ?? ""),
  rcol("Source System", () => "New RBIM"),
  rcol("Import Mode", () => "New Encoding"),
  rcol("Import Status", () => "Clean"),
  rcol("Review Warnings", (r) => stringifyList(readExtra(r.cleanedData, "reviewWarnings"))),
  rcol("Legacy Preserved Raw Row", (r, _m, i) => stringifyJson(readNested(r.cleanedData, ["legacyMigration", "rawRows", i]))),
  rcol("Data Quality Issues", (r) => stringifyList(readExtra(r.cleanedData, "dataQualityIssues"))),
];

const familyUnitHeaders = ["Submission ID", "Household ID", "Household Number", "Family Unit ID", "Family Unit Number", "Family Name", "Member Count", "Family Head Resident ID", "Family Head Name"];

function familyUnitRows(data: RbimDataFile): CellValue[][] {
  return (data.records ?? []).flatMap((record) => {
    const families = new Map<string, Array<{ member: Member; index: number }>>();
    const members = record.rawData?.members ?? [];
    members.forEach((member, index) => {
      if (!member) return;
      const familyNumber = member.familyUnitNo || "FAM-001";
      const group = families.get(familyNumber) ?? [];
      group.push({ member, index });
      families.set(familyNumber, group);
    });
    return [...families.entries()].map(([number, groupMembers]) => {
      const head = groupMembers.find(({ member }) => member && (member.isFamilyHead === true || /^(?:0*1|HEAD)$/i.test(String(member.relationshipToFamilyHead ?? "").trim())));
      const firstMember = groupMembers[0]?.member;
      return [
        record.id ?? "",
        householdId(record),
        record.rawData?.household?.householdCode ?? "",
        firstMember ? familyUnitId(record, firstMember) : "",
        number,
        groupMembers.find(({ member }) => member?.familyName)?.member.familyName ?? "",
        groupMembers.length,
        head ? residentId(record, head.member, head.index) : "",
        head ? [head.member?.firstName, head.member?.middleName, head.member?.lastName].filter(Boolean).join(" ") : "",
      ];
    });
  });
}

function exportDictionaryRows(): CellValue[][] {
  return [
    ...householdColumns.map((column, index): CellValue[] => ["Households", index + 1, column.heading, /^Q\d{1,2}[A-C]?/i.exec(column.heading)?.[0] ?? "", "Household and questionnaire data"]),
    ...residentColumns.map((column, index): CellValue[] => ["Residents", index + 1, column.heading, /^Q\d{1,2}[A-C]?/i.exec(column.heading)?.[0] ?? "", "Individual and family assignment data"]),
    ...familyUnitHeaders.map((heading, index): CellValue[] => ["Family Units", index + 1, heading, "", "Derived family group"]),
    ...completeQuestionnaireHeaders.map((heading, index): CellValue[] => ["Complete Questionnaire", index + 1, heading, /^Q\d{1,2}[A-C]?/i.exec(heading)?.[0] ?? "", "Q1–Q62 combined resident and household response"]),
  ];
}

// One resident per row. Household answers are repeated for every resident so Q1–Q62
// can be reviewed on a single worksheet without dropping household context.
const completeQuestionnaireResidentColumns = residentColumns.filter((column) => /^Q(?:[1-9]|[1-3][0-9]|4[0-5])(?:[A-C])?(?![0-9])/.test(column.heading));
const completeQuestionnaireHouseholdColumns = householdColumns.filter((column) => /^Q(?:4[6-9]|5[0-9]|6[0-2])(?:[A-C])?(?![0-9])/.test(column.heading));
const completeQuestionnaireHeaders = [
  "Submission ID", "Household ID", "Household Number", "Resident ID", "Family Unit ID",
  ...completeQuestionnaireResidentColumns.map((column) => column.heading),
  ...completeQuestionnaireHouseholdColumns.map((column) => column.heading),
];

function completeQuestionnaireRows(data: RbimDataFile): CellValue[][] {
  return (data.records ?? []).flatMap((record) =>
    (record.rawData?.members ?? []).map((member, index) => [
      record.id ?? "",
      householdId(record),
      record.rawData?.household?.householdCode ?? "",
      residentId(record, member, index),
      familyUnitId(record, member),
      ...completeQuestionnaireResidentColumns.map((column) => column.read(record, member, index)),
      ...completeQuestionnaireHouseholdColumns.map((column) => column.read(record)),
    ]),
  );
}

export function createRbimWorkbookBlob(data: RbimDataFile): Blob {
  const records = data.records ?? [];
  const householdRows = records.map((record) =>
    householdColumns.map((column) => column.read(record)),
  );
  const residentRows = records.flatMap((record) =>
    (record.rawData?.members ?? []).map((member, memberIndex) =>
      residentColumns.map((column) => column.read(record, member, memberIndex)),
    ),
  );

  const familyRows = familyUnitRows(data);
  const dictionaryRows = exportDictionaryRows();
  const files: Array<{ name: string; content: string }> = [
    { name: "[Content_Types].xml", content: contentTypesXml() },
    { name: "_rels/.rels", content: rootRelationshipsXml() },
    { name: "xl/workbook.xml", content: workbookXml() },
    { name: "xl/_rels/workbook.xml.rels", content: workbookRelationshipsXml() },
    { name: "xl/styles.xml", content: stylesXml() },
    {
      name: "xl/worksheets/sheet1.xml",
      content: createWorksheetXml(
        householdColumns.map((column) => column.heading),
        householdRows,
      ),
    },
    {
      name: "xl/worksheets/sheet2.xml",
      content: createWorksheetXml(
        residentColumns.map((column) => column.heading),
        residentRows,
      ),
    },
    { name: "xl/worksheets/sheet3.xml", content: createWorksheetXml(familyUnitHeaders, familyRows) },
    { name: "xl/worksheets/sheet4.xml", content: createWorksheetXml(["Worksheet", "Column Number", "Export Heading", "Question", "Description"], dictionaryRows) },
    { name: "xl/worksheets/sheet5.xml", content: createWorksheetXml(completeQuestionnaireHeaders, completeQuestionnaireRows(data)) },
  ];

  return new Blob([createStoredZip(files)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function col(heading: string, read: HouseholdReader): Column<HouseholdReader> {
  return { heading, read };
}

function rcol(heading: string, read: ResidentReader): Column<ResidentReader> {
  return { heading, read };
}

function interviewColumns(index: number, label: string): Array<Column<HouseholdReader>> {
  const read = (record: ExportRecord) => record.rawData?.interviews?.[index];
  return [
    col(`${label} Visit`, (r) => read(r)?.visit ?? ""),
    col(`${label} Date of Visit`, (r) => read(r)?.dateOfVisit ?? ""),
    col(`${label} Time Started`, (r) => read(r)?.timeStart ?? ""),
    col(`${label} Time Ended`, (r) => read(r)?.timeEnd ?? ""),
    col(`${label} Interview Result`, (r) => read(r)?.result ?? ""),
    col(`${label} Date of Next Visit`, (r) => read(r)?.dateOfNextVisit ?? ""),
    col(`${label} Interviewer`, (r) => read(r)?.interviewer ?? ""),
    col(`${label} Interview Supervisor`, (r) => read(r)?.supervisor ?? ""),
  ];
}

function householdQuestionColumns(): Array<Column<HouseholdReader>> {
  const definitions = [
    ["housingTenure", "Q46 Housing Unit Tenure"],
    ["lotTenure", "Q47 Lot Tenure"],
    ["hazardProneArea", "Q48 Hazard-Prone Area"],
    ["disasterExperiencedPastFiveYears", "Q49 Disaster Experienced in Past 5 Years"],
    ["knowsNearestEvacuationCenter", "Q50 Knows Nearest Evacuation Center"],
    ["lightingFuel", "Q51 Fuel Used for Lighting"],
    ["cookingFuel", "Q52 Fuel Used for Cooking"],
    ["drinkingWaterSource", "Q53 Drinking Water Source"],
    ["garbageDisposal", "Q54A Kitchen Garbage Disposal"],
    ["segregatesGarbage", "Q54B Garbage Segregation"],
    ["toiletFacility", "Q55 Toilet Facility"],
    ["buildingType", "Q56 Building Type"],
    ["wallMaterial", "Q57 Wall Material"],
    ["femaleDeathPast12Months", "Q58 Female Death in Past 12 Months"],
    ["femaleDeathAge", "Q58 Female Death Age"],
    ["femaleDeathCause", "Q58 Female Death Cause"],
    ["childDeathPast12Months", "Q59 Child Death in Past 12 Months"],
    ["childDeathAge", "Q59 Child Death Age"],
    ["childDeathSex", "Q59 Child Death Sex"],
    ["childDeathCause", "Q59 Child Death Cause"],
    ["commonDiseaseCause1", "Q60 Common Disease / Cause 1"],
    ["commonDiseaseCause2", "Q60 Common Disease / Cause 2"],
    ["commonDiseaseCause3", "Q60 Common Disease / Cause 3"],
    ["primaryNeed1", "Q61 Primary Need 1"],
    ["primaryNeed2", "Q61 Primary Need 2"],
    ["primaryNeed3", "Q61 Primary Need 3"],
    ["futureBarangay", "Q62 Future Barangay"],
    ["futureMunicipality", "Q62 Future City / Municipality"],
    ["futureProvince", "Q62 Future Province"],
  ] as const;

  return definitions.map(([key, heading]) =>
    col(heading, (record) => record.rawData?.householdQuestions?.[key] ?? ""),
  );
}

function householdId(record: ExportRecord) {
  const household = record.rawData?.household;
  return [
    "hh",
    safeId(household?.householdCode),
    safeId(household?.barangay),
    safeId(household?.municipality),
    safeId(household?.province),
  ].filter(Boolean).join("-");
}

function residentId(record: ExportRecord, member: Member | undefined | null, memberIndex: number) {
  return [
    "res",
    record.id ?? householdId(record),
    String(memberIndex + 1),
    safeId(member?.firstName),
    safeId(member?.middleName),
    safeId(member?.lastName),
  ].filter(Boolean).join("-");
}

function familyUnitId(record: ExportRecord, member: Member | undefined | null) {
  return `fam_${householdId(record)}-${safeId(member?.familyUnitNo || "FAM-001")}`;
}

function uniqueFamilyCount(members: readonly Member[] = []) {
  return new Set((members ?? []).map((member) => member?.familyUnitNo || "FAM-001")).size;
}

function isHouseholdHead(value: string | undefined | null) {
  const normalized = String(value ?? "").trim().toUpperCase();
  return /^(?:0*1(?:\s*[-:]\s*HEAD)?|HEAD)$/.test(normalized);
}

function computeAge(member: Member | undefined | null) {
  if (!member) return { years: "", display: "" };
  if (member.ageSource === "reported" && member.reportedAge !== null && member.reportedAge !== undefined) {
    return { years: member.reportedAge, display: `${member.reportedAge} years` };
  }

  const birthDate = parseDate(member.dateOfBirth ?? "");
  const asOfDate = parseDate(member.ageAsOfDate ?? "") ?? new Date();
  if (!birthDate) {
    if (typeof member.reportedAge === "number") {
      return { years: member.reportedAge, display: `${member.reportedAge} years` };
    }
    return { years: "", display: "" };
  }

  let years = asOfDate.getFullYear() - birthDate.getFullYear();
  let months = asOfDate.getMonth() - birthDate.getMonth();
  if (asOfDate.getDate() < birthDate.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return {
    years: Math.max(0, years),
    display: months > 0 ? `${Math.max(0, years)} years ${months} months` : `${Math.max(0, years)} years`,
  };
}

function parseDate(value: string) {
  const match = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/.exec(value);
  if (!match) return null;
  const parsed = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function safeId(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function readExtra(value: unknown, key: string): CellValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const candidate = (value as Record<string, unknown>)[key];
  return toCellValue(candidate);
}

function readNested(value: unknown, path: Array<string | number>): unknown {
  let current: unknown = value;
  for (const key of path) {
    if (Array.isArray(current) && typeof key === "number") {
      current = current[key];
      continue;
    }
    if (!current || typeof current !== "object" || Array.isArray(current) || typeof key !== "string") {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function toCellValue(value: unknown): CellValue {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  return stringifyJson(value);
}

function stringifyList(value: unknown) {
  if (Array.isArray(value)) return value.map((item) => String(item ?? "")).filter(Boolean).join("; ");
  return value === null || value === undefined ? "" : String(value);
}

function stringifyJson(value: unknown) {
  if (value === null || value === undefined || value === "") return "";
  try {
    return typeof value === "string" ? value : JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function contentTypesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet3.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet4.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet5.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;
}

function rootRelationshipsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
}

function workbookXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Households" sheetId="1" r:id="rId1"/>
    <sheet name="Residents" sheetId="2" r:id="rId2"/>
    <sheet name="Family Units" sheetId="3" r:id="rId3"/>
    <sheet name="Data Dictionary" sheetId="4" r:id="rId4"/>
    <sheet name="Complete Questionnaire" sheetId="5" r:id="rId5"/>
  </sheets>
</workbook>`;
}

function workbookRelationshipsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet3.xml"/>
  <Relationship Id="rId4" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet4.xml"/>
  <Relationship Id="rId5" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet5.xml"/>
  <Relationship Id="rId6" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
}

function stylesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="3">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF123C73"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border><left/><right/><top/><bottom style="hair"><color rgb="FFD7E0EA"/></bottom><diagonal/></border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="4">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;
}

function createWorksheetXml(headers: string[], rows: CellValue[][]) {
  const endColumn = columnName(Math.max(0, headers.length - 1));
  const endRow = Math.max(1, rows.length + 1);
  const widths = headers
    .map((header, index) => {
      const width = Math.min(42, Math.max(14, header.length + 2));
      return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
    })
    .join("");
  const headerRow = createRowXml(1, headers, 1, 34);
  const dataRows = rows.map((row, index) => createRowXml(index + 2, row, 3, 30)).join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>
  <dimension ref="A1:${endColumn}${endRow}"/>
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  <cols>${widths}</cols>
  <sheetData>${headerRow}${dataRows}</sheetData>
  <autoFilter ref="A1:${endColumn}${endRow}"/>
  <pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0"/>
</worksheet>`;
}

function createRowXml(rowNumber: number, values: CellValue[], styleIndex: number, height: number) {
  const cells = values.map((value, columnIndex) =>
    createCellXml(columnIndex, rowNumber, value, styleIndex),
  ).join("");
  return `<row r="${rowNumber}" ht="${height}" customHeight="1">${cells}</row>`;
}

function createCellXml(columnIndex: number, rowNumber: number, value: CellValue, styleIndex: number) {
  const reference = `${columnName(columnIndex)}${rowNumber}`;
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${reference}" s="${styleIndex}"><v>${value}</v></c>`;
  }
  if (typeof value === "boolean") {
    return `<c r="${reference}" s="${styleIndex}" t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  const text = String(value ?? "").slice(0, 32767);
  return `<c r="${reference}" s="${styleIndex}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`;
}

function columnName(index: number) {
  let value = index + 1;
  let result = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }
  return result;
}

function escapeXml(value: string) {
  return value
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function createStoredZip(files: Array<{ name: string; content: string }>) {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const dataBytes = encoder.encode(file.content);
    const crc = crc32(dataBytes);

    const localHeader = new Uint8Array(30);
    const localView = new DataView(localHeader.buffer);
    writeUint32(localView, 0, 0x04034b50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0x0800);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, 0);
    writeUint16(localView, 12, 0);
    writeUint32(localView, 14, crc);
    writeUint32(localView, 18, dataBytes.length);
    writeUint32(localView, 22, dataBytes.length);
    writeUint16(localView, 26, nameBytes.length);
    writeUint16(localView, 28, 0);
    localParts.push(localHeader, nameBytes, dataBytes);

    const centralHeader = new Uint8Array(46);
    const centralView = new DataView(centralHeader.buffer);
    writeUint32(centralView, 0, 0x02014b50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0x0800);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, 0);
    writeUint16(centralView, 14, 0);
    writeUint32(centralView, 16, crc);
    writeUint32(centralView, 20, dataBytes.length);
    writeUint32(centralView, 24, dataBytes.length);
    writeUint16(centralView, 28, nameBytes.length);
    writeUint16(centralView, 30, 0);
    writeUint16(centralView, 32, 0);
    writeUint16(centralView, 34, 0);
    writeUint16(centralView, 36, 0);
    writeUint32(centralView, 38, 0);
    writeUint32(centralView, 42, offset);
    centralParts.push(centralHeader, nameBytes);

    offset += localHeader.length + nameBytes.length + dataBytes.length;
  }

  const centralDirectory = concatBytes(centralParts);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  writeUint32(endView, 0, 0x06054b50);
  writeUint16(endView, 4, 0);
  writeUint16(endView, 6, 0);
  writeUint16(endView, 8, files.length);
  writeUint16(endView, 10, files.length);
  writeUint32(endView, 12, centralDirectory.length);
  writeUint32(endView, 16, offset);
  writeUint16(endView, 20, 0);

  return concatBytes([...localParts, centralDirectory, end]);
}

function concatBytes(parts: Uint8Array[]) {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

function writeUint16(view: DataView, offset: number, value: number) {
  view.setUint16(offset, value, true);
}

function writeUint32(view: DataView, offset: number, value: number) {
  view.setUint32(offset, value >>> 0, true);
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) !== 0 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}
